import { Merchant, W_FORM_TYPES } from "types/Models/merchant";
import { emptyApi } from "./emptyApi";
import { FileUrl } from "types/Models/fileObject";

export const taskMerchantApis = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getTaskMerchants: build.query<
      { merchants: Merchant[]; document_id: string | null },
      {
        taskId: string;
        groupId: string;
        season: string;
        formType?: string;
        entityId: string;
        generate_report?: boolean;
        merchant_ids?: string;
      }
    >({
      query: ({
        taskId,
        groupId,
        season,
        formType,
        entityId,
        merchant_ids,
        generate_report,
      }) => {
        return {
          url: `/api/inkle/tasks/${taskId}/group/${groupId}/entity/${entityId}/merchants/`,
          params: {
            season,
            form_type: formType,
            generate_report,
            merchant_ids,
          },
        };
      },
      providesTags: ["Merchants", "TRANSACTION_MERCHANTS", "VENDORS"],
    }),
    updateTaskMerchant: build.mutation<
      Merchant,
      {
        taskId: string;
        groupId: string;
        merchantId: string;
        email?: string;
        file?: File;
        form_type?: W_FORM_TYPES;
        season: string;
        cc_emails?: string;
      }
    >({
      query: ({
        taskId,
        groupId,
        merchantId,
        email,
        file,
        form_type,
        season,
        cc_emails,
      }) => {
        const params = {
          merchant_id: merchantId,
          email,
          file,
          form_type,
          season,
          cc_emails,
        };

        const formData = new FormData();
        Object.entries(params).forEach(
          ([key, value]) => value && formData.append(key, value)
        );

        return {
          url: `/api/inkle/tasks/${taskId}/group/${groupId}/merchants/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Merchants"],
    }),
    updateMerchantForm: build.mutation<
      Merchant,
      {
        groupId: string;
        merchantId: string;
        email?: string;
        file?: File;
        form_type?: W_FORM_TYPES;
        season: string;
        cc_emails?: string;
      }
    >({
      query: ({
        groupId,
        merchantId,
        email,
        file,
        form_type,
        season,
        cc_emails,
      }) => {
        const params = {
          merchant_id: merchantId,
          email,
          file,
          form_type,
          season,
          cc_emails,
        };

        const formData = new FormData();
        Object.entries(params).forEach(
          ([key, value]) => value && formData.append(key, value)
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/merchant/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Merchants"],
    }),
    updateBulkMerchantEmail: build.mutation<
      Merchant,
      {
        groupId: string;
        payload: { merchants: { email: string; merchant_id: string }[] };
      }
    >({
      query: ({ groupId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/merchants/bulk-update/`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    bulkWformRequest: build.mutation<
      Merchant,
      {
        groupId: string;
        payload: {
          merchant_details: { email: string; merchant_id: string }[];
          season: string;
          cc_emails?: string;
        };
      }
    >({
      query: ({ groupId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/merchant/bulk-request/`,
          method: "POST",
          body: payload,
        };
      },
    }),
    markVendorAsVoid: build.mutation<
      Merchant,
      {
        groupId: string;
        merchantId: string;
        payload: {
          task_id: string;
          season: string;
        };
      }
    >({
      query: ({ groupId, merchantId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/merchant/${merchantId}/void/`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: ["Merchants"],
    }),
    sendCopyToVendor: build.mutation<
      Merchant,
      {
        groupId: string;
        merchantDocMapId: string;
        email: string;
        cc_emails?: string;
      }
    >({
      query: ({ groupId, merchantDocMapId, email, cc_emails }) => {
        return {
          url: `/api/inkle/tasks/group/${groupId}/merchant/document/${merchantDocMapId}/send-1099-form/`,
          method: "post",
          body: { email, cc_emails },
        };
      },
      invalidatesTags: ["Merchants"],
    }),
    getPostmarkTemplate: build.query<
      {
        template_id: string;
        template_subject: string;
        template_body: string;
      },
      {
        templateKey: string;
      }
    >({
      query: ({ templateKey }) =>
        `/api/inkle/company/email-template/?template_key=${templateKey}`,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTaskMerchantsQuery,
  useLazyGetTaskMerchantsQuery,
  useUpdateTaskMerchantMutation,
  useUpdateMerchantFormMutation,
  useUpdateBulkMerchantEmailMutation,
  useBulkWformRequestMutation,
  useMarkVendorAsVoidMutation,
  useSendCopyToVendorMutation,
  useGetPostmarkTemplateQuery,
} = taskMerchantApis;
